import React, { useCallback, useRef } from 'react';
import { MenuItem, Autocomplete } from '@mui/material';
import { ProductFilter, ProductTag } from '../../../types/product.types';
import { ListPagePrimaryFilterRow, ListPageActiveFilters } from '../../common/list-page';
import { RangeNumberInput, type RangeNumberInputHandle } from '../../common/RangeNumberInput';
import CatalogAutocomplete from '../../common/CatalogAutocomplete';
import { useProductFilters, useWeightSlider, UseProductFiltersProps } from './ProductFilters.hooks';
import {
  FilterContainer,
  SecondaryFilterRow,
  StyledTextField,
  WeightContainer,
  WeightLabel,
  WeightSliderContainer,
  StyledSlider,
  WeightUnit,
  FilterCollapse,
} from './ProductFilters.styles';

export interface ProductFiltersProps extends Omit<
  UseProductFiltersProps,
  | 'productTypes'
  | 'suppliers'
  | 'productTags'
  | 'materials'
  | 'filters'
  | 'setFilters'
  | 'appliedFilters'
  | 'onClearAppliedField'
  | 'showMoreFilters'
  | 'onSearch'
  | 'onReset'
  | 'onToggleMoreFilters'
> {
  filters: ProductFilter;
  setFilters: React.Dispatch<React.SetStateAction<ProductFilter>>;
  appliedFilters: ProductFilter;
  onClearAppliedField: (patch: Partial<ProductFilter>) => void;
  showMoreFilters: boolean;
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
  productTypes: { id: number; name: string }[];
  suppliers: { id: number; name: string }[];
  productTags: ProductTag[];
  materials?: { id: number; name: string }[];
  className?: string;
}

const DEFAULT_WEIGHT_MAX = 100;
const ABSOLUTE_WEIGHT_MAX = 1000;

const ProductFiltersComponent: React.FC<ProductFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
  productTypes,
  suppliers,
  productTags,
  materials = [],
  className,
}) => {
  const {
    handleInputChange,
    handleKeyPress,
    renderStatusValue,
    handleTagsChange,
    selectedTags,
    safeProductTags,
    activeFilterChips,
  } = useProductFilters({
    filters,
    setFilters,
    appliedFilters,
    onClearAppliedField,
    showMoreFilters,
    onSearch,
    onReset,
    onToggleMoreFilters,
    productTypes,
    suppliers,
    productTags,
    materials,
  });

  const {
    tempValues,
    sliderMax,
    handleSliderChange,
    handleSliderCommitted,
    handleMinWeightChange,
    handleMaxWeightChange,
  } = useWeightSlider(filters.minWeight, filters.maxWeight, setFilters);

  const weightMinRef = useRef<RangeNumberInputHandle>(null);
  const weightMaxRef = useRef<RangeNumberInputHandle>(null);

  const handleSearchWithRanges = useCallback(() => {
    weightMinRef.current?.commit();
    weightMaxRef.current?.commit();
    onSearch();
  }, [onSearch]);

  return (
    <FilterContainer className={className}>
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={onToggleMoreFilters}
        onReset={onReset}
        onSearch={handleSearchWithRanges}
        searchFieldProps={{
          label: 'Buscar Producto',
          name: 'searchTerm',
          value: filters.searchTerm || '',
          onChange: handleInputChange,
          onKeyDown: handleKeyPress,
        }}
      />

      {/* Segunda fila - Filtros adicionales */}
      <FilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <SecondaryFilterRow>
          {/* Tipo de Producto */}
          <CatalogAutocomplete
            catalogType="category"
            value={filters.productType}
            onChange={(value) => setFilters((prev) => ({ ...prev, productType: value }))}
            pinOptions={productTypes}
            label="Tipo de Producto"
            size="medium"
            textFieldProps={{ variant: 'outlined', fullWidth: true }}
          />

          {/* Estado */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Estado"
            name="productStatus"
            value={filters.productStatus || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderStatusValue }}
          >
            <MenuItem value="ACTIVE">Activo</MenuItem>
            <MenuItem value="INACTIVE">Inactivo</MenuItem>
          </StyledTextField>

          {/* Proveedor */}
          <CatalogAutocomplete
            catalogType="supplier"
            value={filters.supplierId}
            onChange={(value) => setFilters((prev) => ({ ...prev, supplierId: value }))}
            pinOptions={suppliers}
            label="Proveedor"
            size="medium"
            textFieldProps={{ variant: 'outlined', fullWidth: true }}
          />

          {/* Etiqueta */}
          <Autocomplete
            multiple
            options={safeProductTags}
            getOptionLabel={(option) => option.name}
            value={selectedTags}
            onChange={handleTagsChange}
            size="medium"
            noOptionsText="Sin coincidencias"
            ListboxProps={{
              style: {
                maxHeight: '200px', // Aproximadamente 10 elementos
              },
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                fullWidth
                variant="outlined"
                label="Etiqueta"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Material */}
          <CatalogAutocomplete
            catalogType="material"
            value={filters.materialId}
            onChange={(value) => setFilters((prev) => ({ ...prev, materialId: value }))}
            pinOptions={materials}
            label="Material"
            size="medium"
            textFieldProps={{ variant: 'outlined', fullWidth: true }}
          />

          {/* Talla */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Talla"
            name="diameterSize"
            value={filters.diameterSize || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            InputLabelProps={{ shrink: true }}
          />

          {/* Color */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Color"
            name="color"
            value={filters.color || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            InputLabelProps={{ shrink: true }}
          />

          {/* Código Proveedor */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Código Proveedor"
            name="workshopCode"
            value={filters.workshopCode || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            InputLabelProps={{ shrink: true }}
          />

          {/* Peso */}
          <WeightContainer>
            <WeightLabel>Peso (g)</WeightLabel>
            <WeightSliderContainer>
              <RangeNumberInput
                ref={weightMinRef}
                value={filters.minWeight}
                defaultDisplayValue={0}
                onCommit={handleMinWeightChange}
                onEnter={(v) => {
                  handleMinWeightChange(v);
                  onSearch();
                }}
                max={ABSOLUTE_WEIGHT_MAX}
              />
              <StyledSlider
                value={tempValues}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderCommitted}
                valueLabelDisplay="auto"
                min={0}
                max={sliderMax}
                step={10}
              />
              <RangeNumberInput
                ref={weightMaxRef}
                value={filters.maxWeight}
                defaultDisplayValue={DEFAULT_WEIGHT_MAX}
                onCommit={handleMaxWeightChange}
                onEnter={(v) => {
                  handleMaxWeightChange(v);
                  onSearch();
                }}
                max={ABSOLUTE_WEIGHT_MAX}
              />
              <WeightUnit>g</WeightUnit>
            </WeightSliderContainer>
          </WeightContainer>
        </SecondaryFilterRow>
      </FilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </FilterContainer>
  );
};

export default React.memo(ProductFiltersComponent);
